import {
  ConfirmDialog,
  customAlertApiRef,
  WbButton,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import { builderDpSnapshotCreatePermission } from '@agilelab/plugin-wb-rbac-common';
import { stringifyEntityRef } from '@backstage/catalog-model';
import { Progress } from '@backstage/core-components';
import { useApi, useRouteRef } from '@backstage/core-plugin-api';
import { usePermission } from '@backstage/plugin-permission-react';
import { Box, Tooltip, Typography } from '@material-ui/core';
import AddCircle from '@material-ui/icons/AddCircle';
import { useState } from 'react';
import { panelCatalogApiRef } from '../../api';
import { releaseDetailRouteRef } from '../../routes';
import { generateSnapshotButtonTooltip, getReleaseInfo } from '../../utils';
import { useEditorPageContext } from './context/useEditorPageContext';

export const NewSnapshotButton = () => {
  const [isCreatingSnapshot, setIsCreatingSnapshot] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const panelCatalog = useApi(panelCatalogApiRef);
  const alertApi = useApi(customAlertApiRef);

  const {
    releases,
    fetchReleaseState,
    entity,
    setVersion,
    environment,
    addRelease,
    entitiesAndBranches,
    setCurrRelease,
    actionError,
    oldDescriptorFound,
    processorErrors,
  } = useEditorPageContext();

  const releaseDetailRoute = useRouteRef(releaseDetailRouteRef);

  const { allowed: canCreateSnapshot } = usePermission({
    permission: builderDpSnapshotCreatePermission,
    resourceRef: stringifyEntityRef({
      kind: entity?.kind ?? 'system',
      namespace: 'default',
      name: entity?.metadata.name ?? '',
    }),
  });

  const createSnapshot = async () => {
    try {
      if (!entity) return;

      const entities = Object.entries(entitiesAndBranches || {}).map(
        ([key, value]) => ({ entityRef: key, branch: value }),
      );

      setIsCreatingSnapshot(true);
      const newRelease = await panelCatalog.createSnapshot(
        entity.metadata.name,
        entity.kind,
        entities,
      );

      // FIXME: This is a hack to force the descriptor to be re-fetched
      const url = new URL(window.location.href);
      url.searchParams.set('bypassCache', 'true');
      window.history.replaceState(window.history.state, '', url);

      setVersion?.(newRelease.metadata.version);
      setCurrRelease(newRelease);
      addRelease(newRelease);

      const { kind, namespace, name } = getReleaseInfo(newRelease);

      alertApi.post({
        actions: (
          <WbLink
            route={releaseDetailRoute({ kind, namespace, name })}
            text={`Open V${newRelease?.metadata?.version}`}
          />
        ),
        message: 'New Draft-Release created successfully.',
        severity: 'success',
      });
    } catch (error) {
      alertApi.post({
        error,
        severity: 'error',
      });
    } finally {
      setIsCreatingSnapshot(false);
    }
  };

  if (fetchReleaseState?.loading) {
    return (
      <div style={{ width: '100%' }}>
        <Progress />
      </div>
    );
  }

  if (!releases || !environment) return null;

  return (
    <Box style={{ paddingRight: '5px' }}>
      <Tooltip
        title={generateSnapshotButtonTooltip(
          canCreateSnapshot,
          false,
          processorErrors,
          false,
          actionError,
        )}
      >
        <span>
          <WbButton
            label="New draft release"
            disabled={
              isCreatingSnapshot ||
              !canCreateSnapshot ||
              !!actionError ||
              !!processorErrors?.length
            }
            onClick={() =>
              oldDescriptorFound ? setOpenDialog(true) : createSnapshot()
            }
            loading={isCreatingSnapshot}
            icon={<AddCircle />}
          />
        </span>
      </Tooltip>
      <ConfirmDialog
        title="Descriptor not updated!"
        description={
          <>
            <Typography>
              A new version of descriptor have been found. The release will be
              created from the last version (that could differ from the one you
              see here). Click on "Refresh" to see the last version of the
              descriptor.
            </Typography>
          </>
        }
        open={openDialog}
        onConfirm={createSnapshot}
        onClose={() => setOpenDialog(false)}
        inProgress={isCreatingSnapshot}
        confirmButtonText="Yes, create"
      />
    </Box>
  );
};
